import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, getCurrentUser } from "@/lib/apiClient";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  User,
  Calendar,
  ClipboardCheck,
  Plus,
  FileText,
  Activity,
} from "lucide-react";
import { DefectFormDialog } from "./DefectFormDialog";
import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface QualityInspectionDetailsProps {
  inspectionId: string;
  onBack: () => void;
}

export function QualityInspectionDetails({ inspectionId, onBack }: QualityInspectionDetailsProps) {
  const { language } = useLanguage();
  const { userRole } = useAuth();
  const locale = language === "es" ? es : enUS;
  const queryClient = useQueryClient();
  const [defectDialogOpen, setDefectDialogOpen] = useState(false);

  const { data: inspection, isLoading } = useQuery({
    queryKey: ["quality-inspection", inspectionId],
    queryFn: async () => {
      const { data, error } = await api
        .from("quality_inspections")
        .select(`
          *,
          work_orders(order_number, product_name, customer),
          production_batches(batch_number, quantity),
          profiles(full_name, email)
        `)
        .eq("id", inspectionId)
        .single();

      if (error) throw error;
      return data;
    },
  });

  const { data: defects } = useQuery({
    queryKey: ["quality-defects", inspectionId],
    queryFn: async () => {
      const { data, error } = await api
        .from("quality_defects")
        .select("*")
        .eq("inspection_id", inspectionId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const { data: criteria } = useQuery({
    queryKey: ["quality-criteria"],
    queryFn: async () => {
      const { data, error } = await api
        .from("quality_criteria")
        .select("*")
        .eq("is_active", true)
        .order("sequence_order");

      if (error) throw error;
      return data;
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      const user = getCurrentUser();
      const updates: any = { status: newStatus };

      if (newStatus === "passed" || newStatus === "failed") {
        updates.approved_by = user?.id;
        updates.approved_at = new Date().toISOString();
      }

      const { error } = await api
        .from("quality_inspections")
        .update(updates)
        .eq("id", inspectionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quality-inspection", inspectionId] });
      queryClient.invalidateQueries({ queryKey: ["quality-inspections"] });
      toast.success("Estado actualizado correctamente");
    },
    onError: (error: any) => {
      toast.error(error.message || "Error al actualizar estado");
    },
  });

  const canManage = userRole === "admin" || userRole === "supervisor" || userRole === "quality";

  const getStatusConfig = (status: string) => {
    const config: Record<string, { label: string; color: string; icon: any }> = {
      pending: { label: "Pendiente", color: "bg-gray-500/10 text-gray-500", icon: Clock },
      in_progress: { label: "En Progreso", color: "bg-blue-500/10 text-blue-500", icon: Activity },
      passed: { label: "Aprobado", color: "bg-green-500/10 text-green-500", icon: CheckCircle2 },
      failed: { label: "Rechazado", color: "bg-red-500/10 text-red-500", icon: XCircle },
      conditional: { label: "Condicional", color: "bg-yellow-500/10 text-yellow-500", icon: AlertTriangle },
    };
    return config[status] || config.pending;
  };

  const getSeverityConfig = (severity: string) => {
    const config: Record<string, { label: string; color: string }> = {
      critical: { label: "Critico", color: "bg-red-500 text-white" },
      major: { label: "Mayor", color: "bg-orange-500 text-white" },
      minor: { label: "Menor", color: "bg-yellow-500 text-black" },
      cosmetic: { label: "Cosmetico", color: "bg-blue-500 text-white" },
    };
    return config[severity] || config.minor;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!inspection) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Inspeccion no encontrada</p>
        <Button onClick={onBack} className="mt-4">
          Volver
        </Button>
      </div>
    );
  }

  const statusConfig = getStatusConfig(inspection.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">
            Inspeccion de Calidad
          </h1>
          <p className="text-muted-foreground">
            {inspection.work_orders?.product_name || "Sin producto"}
          </p>
        </div>
        <Badge className={cn("gap-1 text-sm py-1 px-3", statusConfig.color)}>
          <StatusIcon className="w-4 h-4" />
          {statusConfig.label}
        </Badge>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* General Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Informacion General
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Orden de Trabajo</p>
                  <p className="font-medium">{inspection.work_orders?.order_number || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Producto</p>
                  <p className="font-medium">{inspection.work_orders?.product_name || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Lote</p>
                  <p className="font-medium">{inspection.production_batches?.batch_number || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Cliente</p>
                  <p className="font-medium">{inspection.work_orders?.customer || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tipo de Inspeccion</p>
                  <p className="font-medium capitalize">{inspection.inspection_type?.replace("_", " ") || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Fecha</p>
                  <p className="font-medium">
                    {format(new Date(inspection.inspection_date), "PPP", { locale })}
                  </p>
                </div>
              </div>

              {inspection.notes && (
                <>
                  <Separator className="my-4" />
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Notas</p>
                    <p className="text-sm">{inspection.notes}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Defects Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Defectos Encontrados ({defects?.length || 0})
                </CardTitle>
                {canManage && inspection.status === "in_progress" && (
                  <Button size="sm" onClick={() => setDefectDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-1" />
                    Registrar Defecto
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {defects && defects.length > 0 ? (
                <div className="space-y-3">
                  {defects.map((defect: any) => {
                    const severityConfig = getSeverityConfig(defect.severity);
                    return (
                      <div
                        key={defect.id}
                        className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30"
                      >
                        <Badge className={severityConfig.color}>
                          {severityConfig.label}
                        </Badge>
                        <div className="flex-1">
                          <p className="font-medium">{defect.defect_type}</p>
                          {defect.description && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {defect.description}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            {defect.location && <span>Ubicacion: {defect.location}</span>}
                            {defect.quantity > 1 && <span>Cantidad: {defect.quantity}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                  <p>No se han registrado defectos</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quality Criteria */}
          {criteria && criteria.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5" />
                  Criterios de Calidad
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {criteria.map((criterion: any) => (
                    <div
                      key={criterion.id}
                      className="flex items-center justify-between p-3 rounded-lg border"
                    >
                      <div>
                        <p className="font-medium">{criterion.name}</p>
                        {criterion.description && (
                          <p className="text-sm text-muted-foreground">{criterion.description}</p>
                        )}
                      </div>
                      <Badge variant="outline">{criterion.category}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Inspector Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="w-4 h-4" />
                Inspector
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-medium">
                {inspection.profiles?.full_name || inspection.profiles?.email || "Sin asignar"}
              </p>
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                <Calendar className="w-3 h-3" />
                {format(new Date(inspection.inspection_date), "PPp", { locale })}
              </p>
            </CardContent>
          </Card>

          {/* Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Resumen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Muestras Inspeccionadas</span>
                <span className="font-medium">{inspection.samples_inspected || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Muestras Aprobadas</span>
                <span className="font-medium text-green-600">{inspection.samples_passed || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Muestras Rechazadas</span>
                <span className="font-medium text-red-600">{inspection.samples_failed || "-"}</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">Defectos Encontrados</span>
                <span className="font-bold text-yellow-600">{inspection.defects_found || 0}</span>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          {canManage && inspection.status === "in_progress" && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Acciones</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={() => updateStatusMutation.mutate("passed")}
                  disabled={updateStatusMutation.isPending}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Aprobar Inspeccion
                </Button>
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => updateStatusMutation.mutate("failed")}
                  disabled={updateStatusMutation.isPending}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Rechazar Inspeccion
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => updateStatusMutation.mutate("conditional")}
                  disabled={updateStatusMutation.isPending}
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Aprobacion Condicional
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Approval Info */}
          {inspection.approved_by && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Aprobacion</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">Aprobado por</p>
                <p className="font-medium">{inspection.approved_by}</p>
                {inspection.approved_at && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {format(new Date(inspection.approved_at), "PPp", { locale })}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <DefectFormDialog
        open={defectDialogOpen}
        onOpenChange={setDefectDialogOpen}
        inspectionId={inspectionId}
      />
    </div>
  );
}
